// Build operations (IMPLEMENTATION §0.2 R10/R11/R13/R14, §2.7): buying from the shop (place, buy+merge),
// Build-mode moves, swaps, machine merges and sells, chute painting / erasing, rotation, and build undo.
// Each `apply*` runs AFTER its pure resolver (rules/drop, rules/paint, planUndoBuild) accepted the op, so
// it never has to refuse. Build undo restores positions, levels, facing and cash only: re-created buildings
// keep their original ids and come back empty (R13).
import { isProcessorType, maxLevelOf, packerDef, slotCap, slotFams } from './bdefs.ts';
import {
  addBuilding,
  makeChute,
  makeDeposit,
  makeOfType,
  moveBuilding,
  removeBuilding,
  restoreBuilding,
  setFacing,
} from './buildings.ts';
import type { RejectReason } from './commands.ts';
import { drainContents, ejectOrSell, revertProc, takeFromCrate } from './containers.ts';
import { rotateCw } from './core/grid.ts';
import { applyPermille } from './core/num.ts';
import type { Cell, Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { findBuilding, findItem, moveItem } from './entities.ts';
import { cancelGhost, newGhost } from './ghosts.ts';
import { isFree, isOpen, occAt } from './occupancy.ts';
import type { DropOutcome } from './rules/drop.ts';
import type { PaintPlan } from './rules/paint.ts';
import { raiseStall } from './stall.ts';
import type {
  Building,
  BuildOp,
  BuildSnap,
  CrateB,
  ItemRef,
  PackerB,
  ProcessorB,
  SimState,
} from './state.ts';
import { purgeUndo, pushUndo, snapOf } from './undoStack.ts';

type Out<K extends DropOutcome['k']> = Extract<DropOutcome, { k: K }>;

function markBuilt(c: Ctx, b: Building): void {
  if (b.ghost === null && !c.s.progress.firstBuilt.includes(b.type)) {
    c.s.progress.firstBuilt.push(b.type);
  }
}

function placeEvent(c: Ctx, b: Building): void {
  c.ev.push({
    e: 'place',
    bid: b.id,
    type: b.type,
    x: b.x,
    y: b.y,
    dir: b.dir,
    ghost: b.ghost !== null,
  });
}

/** Shop card on empty floor: pay, place (a ghost for the first build of a type), one undo entry. */
export function applyBuyPlace(c: Ctx, o: Out<'buyPlace'>): void {
  c.s.wallet.cash -= o.price;
  const b: Building =
    o.type === 'deposit'
      ? makeDeposit(c.s, o.ore as NonNullable<typeof o.ore>, o.x, o.y, 1)
      : makeOfType(c.s, c.d, o.type, o.x, o.y, o.dir, 1);
  b.paid = o.price;
  if (o.ghost) {
    b.ghost = newGhost(c.d, b.type);
    raiseStall(c, b, 'GHOST');
  }
  addBuilding(c, b);
  markBuilt(c, b);
  pushUndo(c, { k: 'place', cash: o.price, bids: [b.id], snaps: [], item: null });
  placeEvent(c, b);
}

/** A Packer's target follows its level cap unless the player lowered it (R5 "default = the cap"). */
function levelUp(c: Ctx, b: Building): void {
  const was = b.level;
  b.level = was + 1;
  if (b.type === 'packer') {
    const caps = packerDef(c.d).targetCapByLevel;
    if (b.target === caps[was - 1]) b.target = caps[b.level - 1] ?? b.target;
  }
}

/** Shop card on an identical L1 building: pay and merge (not undoable; sell refunds 100%). */
export function applyBuyMerge(c: Ctx, o: Out<'buyMerge'>): void {
  const t = findBuilding(c.s, o.bid) as Building;
  c.s.wallet.cash -= o.price;
  t.paid += o.price;
  levelUp(c, t);
  purgeUndo(c, [t.id]);
  c.ev.push({ e: 'bmerge', bid: t.id, other: 0, level: t.level });
}

/** Build-mode move (the loose item on the target cell, if any, swaps to the origin). */
export function applyBmove(c: Ctx, o: Out<'bmove'>): void {
  const b = findBuilding(c.s, o.bid) as Building;
  const before = snapOf(b);
  const it = o.item > 0 ? findItem(c.s, o.item) : null;
  moveBuilding(c, b, o.x, o.y);
  if (it !== null) {
    moveItem(c, it, before.x, before.y);
    c.ev.push({
      e: 'move',
      id: it.id,
      x: before.x,
      y: before.y,
      fromX: o.x,
      fromY: o.y,
      tag: null,
    });
  }
  pushUndo(c, {
    k: 'move',
    cash: 0,
    bids: [b.id],
    snaps: [before],
    item: it !== null ? { id: it.id, x: o.x, y: o.y } : null,
  });
  c.ev.push({ e: 'bmove', bid: b.id, x: o.x, y: o.y, fromX: before.x, fromY: before.y });
}

function swapBuildings(c: Ctx, a: Building, b: Building): void {
  const ax = a.x;
  const ay = a.y;
  const bx = b.x;
  const by = b.y;
  moveBuilding(c, a, bx, by);
  moveBuilding(c, b, ax, ay);
  c.ev.push({ e: 'bmove', bid: a.id, x: bx, y: by, fromX: ax, fromY: ay });
  c.ev.push({ e: 'bmove', bid: b.id, x: ax, y: ay, fromX: bx, fromY: by });
}

export function applyBswap(c: Ctx, o: Out<'bswap'>): void {
  const a = findBuilding(c.s, o.a) as Building;
  const b = findBuilding(c.s, o.b) as Building;
  const snaps = [snapOf(a), snapOf(b)];
  swapBuildings(c, a, b);
  pushUndo(c, { k: 'swap', cash: 0, bids: [a.id, b.id], snaps, item: null });
}

/** Adds `r` to `xs` when below `cap`, else to the overflow list. */
function keepOr(xs: ItemRef[], cap: Int, r: ItemRef, over: ItemRef[]): void {
  if (xs.length < cap) xs.push(r);
  else over.push(r);
}

function mergeProcessors(c: Ctx, src: ProcessorB, t: ProcessorB, over: ItemRef[]): void {
  const fams = slotFams(c.d, t);
  const cap = slotCap(c.d, t.type);
  const place = (r: ItemRef): void => {
    const i = fams.indexOf(r.fam);
    if (i < 0) over.push(r);
    else keepOr(t.slots[i] as ItemRef[], cap, r, over);
  };
  for (const r of revertProc(c, src)) place(r); // the running cycle's set comes back as inputs
  for (const slot of src.slots) for (const r of slot) place(r);
  for (const r of src.out) keepOr(t.out, c.d.machineRules.outCap, r, over);
  if (t.type === 'smelter') {
    const heatCap = c.d.buildings.smelter.heatCap[t.level - 1] ?? 0;
    const heat = t.heat + src.heat;
    t.heat = heat < heatCap ? heat : heatCap; // surplus heat is lost (heat is not a material)
  }
}

function mergePackers(c: Ctx, src: PackerB, t: PackerB, over: ItemRef[]): void {
  const buffer = packerDef(c.d).buffer;
  for (const r of src.buf) {
    if (t.lockFam === null || t.lockFam === r.fam) {
      if (t.buf.length < buffer) {
        t.lockFam = r.fam;
        t.buf.push(r);
        continue;
      }
    }
    over.push(r);
  }
  for (const r of src.out) {
    if ((t.lockFam === null || t.lockFam === r.fam) && t.out.length < c.d.machineRules.outCap) {
      t.lockFam = t.lockFam ?? r.fam;
      t.out.push(r);
    } else over.push(r);
  }
}

function mergeCrates(c: Ctx, src: CrateB, t: CrateB, over: ItemRef[]): void {
  if (src.fam === null) return;
  const cap = c.d.buildings.crate.capacity[t.level - 1] ?? 0;
  let total = 0;
  for (const k of t.counts) total += k;
  const compatible = t.fam === null || t.fam === src.fam;
  for (let tier = src.counts.length; tier >= 1; tier--) {
    while ((src.counts[tier - 1] ?? 0) > 0) {
      if (compatible && total < cap) {
        src.counts[tier - 1] = (src.counts[tier - 1] ?? 0) - 1;
        t.fam = src.fam;
        t.counts[tier - 1] = (t.counts[tier - 1] ?? 0) + 1;
        total++;
      } else {
        over.push(takeFromCrate(c, src, tier));
      }
    }
  }
}

/**
 * Machine merge (R11, Build only): `bid` merges into `into` → level + 1 at the target's cell and facing;
 * paid is summed; buffers combine up to capacity; overflow goes to free adjacent tiles, else is sold at
 * 100% (manual income). Not undoable: every undo entry naming either building is dropped.
 */
export function applyBmerge(c: Ctx, o: Out<'bmerge'>): void {
  const t = findBuilding(c.s, o.into) as Building;
  const src = removeBuilding(c, o.bid);
  const over: ItemRef[] = [];
  t.paid += src.paid;
  levelUp(c, t);
  if (src.type === 'crate' && t.type === 'crate') mergeCrates(c, src, t, over);
  else if (src.type === 'packer' && t.type === 'packer') mergePackers(c, src, t, over);
  else if (isProcessorType(src.type) && t.type === src.type) {
    mergeProcessors(c, src as ProcessorB, t as ProcessorB, over);
  } else over.push(...drainContents(c, src));
  ejectOrSell(c, over, t.x, t.y);
  purgeUndo(c, [src.id, t.id]);
  c.ev.push({ e: 'bmerge', bid: t.id, other: src.id, level: t.level });
  if (t.level > maxLevelOf(c.d, t)) throw new Error('bmerge past the max level');
}

/** Sells a finished building (not a ghost, not a chute): 100% refund, contents ejected or sold. */
export function sellBuildingNow(c: Ctx, b: Building): Int {
  const refund = applyPermille(b.paid, c.d.economy.buildRefundPermille);
  const snap = snapOf(b);
  removeBuilding(c, b.id);
  c.s.wallet.cash += refund;
  ejectOrSell(c, drainContents(c, b), b.x, b.y);
  pushUndo(c, { k: 'sell', cash: refund, bids: [], snaps: [snap], item: null });
  c.ev.push({ e: 'bremove', bid: b.id, refund });
  return refund;
}

/** Build Sell tool / sellBuilding on any building: a ghost is cancelled, a chute erased, else sold. */
export function applyBsell(c: Ctx, o: Out<'bsell'>): void {
  const b = findBuilding(c.s, o.bid) as Building;
  if (b.ghost !== null) cancelGhost(c, b);
  else if (b.type === 'chute') eraseChutes(c, [{ x: b.x, y: b.y }]);
  else sellBuildingNow(c, b);
}

export function applyPaint(c: Ctx, plan: Extract<PaintPlan, { ok: true }>): void {
  c.s.wallet.cash -= plan.cost;
  const bids: Int[] = [];
  const snaps: BuildSnap[] = [];
  for (const st of plan.steps) {
    if (st.chute > 0) {
      const b = findBuilding(c.s, st.chute) as Building;
      if (b.dir === st.dir && b.type === 'chute' && b.inDir === st.inDir) continue;
      snaps.push(snapOf(b));
      setFacing(c, b, st.dir, st.inDir);
      c.ev.push({ e: 'brotate', bid: b.id, dir: st.dir });
      continue;
    }
    const ch = makeChute(c.s, st.x, st.y, st.dir, st.inDir);
    ch.paid = plan.price;
    addBuilding(c, ch);
    markBuilt(c, ch);
    bids.push(ch.id);
    placeEvent(c, ch);
  }
  if (bids.length > 0 || snaps.length > 0) {
    pushUndo(c, { k: 'paint', cash: plan.cost, bids, snaps, item: null });
  }
}

/**
 * Removes a chute (erase, sell, paint undo). Its carried item goes to a free N/E/S/W neighbour that is not
 * one of `cleared` (the cells of every chute this operation removes, so an erase undo finds them free),
 * else it is sold at 100% (the overflow rule). Returns the refund.
 */
function removeChute(c: Ctx, b: Building, cleared: readonly Cell[]): Int {
  removeBuilding(c, b.id);
  ejectOrSell(c, drainContents(c, b), b.x, b.y, cleared);
  const refund = applyPermille(b.paid, c.d.economy.buildRefundPermille);
  c.ev.push({ e: 'bremove', bid: b.id, refund });
  return refund;
}

/** The chutes on `cells` (other and malformed cells are ignored), in `cells` order. */
function chutesOn(c: Ctx, cells: readonly Cell[]): Building[] {
  const out: Building[] = [];
  for (const p of cells) {
    if (typeof p !== 'object' || p === null || !isOpen(c.occ, p.x, p.y)) continue;
    const id = occAt(c.occ, p.x, p.y);
    const b = id > 0 ? findBuilding(c.s, id) : null;
    if (b !== null && b.type === 'chute' && !out.includes(b)) out.push(b);
  }
  return out;
}

/** Erases the chutes on `cells` (other cells are ignored). Returns how many were removed. */
export function eraseChutes(c: Ctx, cells: readonly Cell[]): Int {
  const chutes = chutesOn(c, cells);
  if (chutes.length === 0) return 0;
  const cleared = chutes.map((b) => ({ x: b.x, y: b.y }));
  const snaps: BuildSnap[] = [];
  let refund = 0;
  for (const b of chutes) {
    snaps.push(snapOf(b));
    refund += removeChute(c, b, cleared);
  }
  c.s.wallet.cash += refund;
  pushUndo(c, { k: 'erase', cash: refund, bids: [], snaps, item: null });
  return snaps.length;
}

/** Chutes erasable on `cells` (pure; the command's validation). */
export function erasableCount(c: Ctx, cells: readonly Cell[]): Int {
  return chutesOn(c, cells).length;
}

/** Rotate (card button / 3 s handle, both modes): 90° clockwise; a chute turns its in-side too. */
export function rotateBuilding(c: Ctx, b: Building): void {
  setFacing(c, b, rotateCw(b.dir), b.type === 'chute' ? rotateCw(b.inDir) : null);
  c.ev.push({ e: 'brotate', bid: b.id, dir: b.dir });
}

// ---------------------------------------------------------------------------------------------------------
// Build undo (R13)

function rebuild(c: Ctx, sn: BuildSnap): Building {
  const b =
    sn.type === 'deposit'
      ? makeDeposit(c.s, sn.ore as NonNullable<BuildSnap['ore']>, sn.x, sn.y, sn.level)
      : sn.type === 'chute'
        ? makeChute(c.s, sn.x, sn.y, sn.dir, sn.inDir ?? sn.dir)
        : makeOfType(c.s, c.d, sn.type, sn.x, sn.y, sn.dir, sn.level);
  b.id = sn.id; // original id (the fresh one drawn by the constructor stays unused)
  b.paid = sn.paid;
  return b;
}

/** Why the newest build-undo entry can't be undone now, or null. Pure. */
export function undoBuildBlock(s: SimState, c: Ctx): RejectReason | null {
  const op = s.transient.undo[s.transient.undo.length - 1];
  if (op === undefined) return 'stale';
  switch (op.k) {
    case 'place':
      return findBuilding(s, op.bids[0] ?? 0) === null ? 'stale' : null;
    case 'move': {
      const b = findBuilding(s, op.bids[0] ?? 0);
      const sn = op.snaps[0];
      if (b === null || sn === undefined) return 'stale';
      const at = occAt(c.occ, sn.x, sn.y);
      if (at === 0) return null;
      return op.item !== null && at === op.item.id ? null : 'noRoom';
    }
    case 'swap': {
      const a = findBuilding(s, op.bids[0] ?? 0);
      const b = findBuilding(s, op.bids[1] ?? 0);
      const sa = op.snaps[0];
      const sb = op.snaps[1];
      if (a === null || b === null || sa === undefined || sb === undefined) return 'stale';
      return a.x === sb.x && a.y === sb.y && b.x === sa.x && b.y === sa.y ? null : 'stale';
    }
    case 'paint':
      for (const id of op.bids) if (findBuilding(s, id) === null) return 'stale';
      return null;
    case 'erase':
    case 'sell':
      if (s.wallet.cash < op.cash) return 'spent';
      for (const sn of op.snaps) if (!isFree(c.occ, sn.x, sn.y)) return 'noRoom';
      return null;
  }
}

/** Applies the newest build-undo entry (validated by undoBuildBlock) and pops it. */
export function applyUndoBuild(c: Ctx): void {
  const st = c.s.transient.undo;
  const op = st.pop() as BuildOp;
  switch (op.k) {
    case 'place': {
      const b = findBuilding(c.s, op.bids[0] ?? 0) as Building;
      if (b.ghost !== null) {
        cancelGhost(c, b);
        return;
      }
      removeBuilding(c, b.id);
      c.s.wallet.cash += op.cash;
      ejectOrSell(c, drainContents(c, b), b.x, b.y);
      c.ev.push({ e: 'bremove', bid: b.id, refund: op.cash });
      return;
    }
    case 'move': {
      const b = findBuilding(c.s, op.bids[0] ?? 0) as Building;
      const sn = op.snaps[0] as BuildSnap;
      const fx = b.x;
      const fy = b.y;
      const it = op.item !== null ? findItem(c.s, op.item.id) : null;
      moveBuilding(c, b, sn.x, sn.y);
      if (it !== null && it.x === sn.x && it.y === sn.y) {
        moveItem(c, it, fx, fy);
        c.ev.push({ e: 'move', id: it.id, x: fx, y: fy, fromX: sn.x, fromY: sn.y, tag: null });
      }
      c.ev.push({ e: 'bmove', bid: b.id, x: sn.x, y: sn.y, fromX: fx, fromY: fy });
      return;
    }
    case 'swap':
      swapBuildings(
        c,
        findBuilding(c.s, op.bids[0] ?? 0) as Building,
        findBuilding(c.s, op.bids[1] ?? 0) as Building,
      );
      return;
    case 'paint': {
      const painted = op.bids.map((id) => findBuilding(c.s, id) as Building);
      const cleared = painted.map((b) => ({ x: b.x, y: b.y }));
      for (const b of painted) removeChute(c, b, cleared);
      for (const sn of op.snaps) {
        const b = findBuilding(c.s, sn.id);
        if (b !== null && b.type === 'chute') {
          setFacing(c, b, sn.dir, sn.inDir);
          c.ev.push({ e: 'brotate', bid: b.id, dir: b.dir });
        }
      }
      c.s.wallet.cash += op.cash;
      return;
    }
    case 'erase':
    case 'sell': {
      c.s.wallet.cash -= op.cash;
      for (const sn of op.snaps) {
        const b = rebuild(c, sn);
        restoreBuilding(c, b);
        markBuilt(c, b);
        placeEvent(c, b);
      }
      return;
    }
  }
}
